/**
 * 视图设置服务端 API（user-view-settings-persistence）。
 * 实现 @cms/ui 的 ViewSettingsApi 注入契约：get 无会话/失败返回 null（降级 localStorage），
 * save 失败静默（localStorage 兜底），不阻塞渲染、不触发登录跳转。
 */
import type { ViewSettingsOutput } from '@cms/shared'
import type { ViewSettingsApi } from '@cms/ui/view-settings'
import { callFunction, isEmployeeSessionRetiring } from '../utils/tcb'
import { getSessionToken } from '../utils/auth-session'
import { getPageSessionScope } from '../utils/session-scope'

// 该服务端接口只接受员工身份；平台沿用本地设置，不能借用共存的员工token。
// 同时检查key，防止平台的延迟保存任务在切回企业页面后写入错误身份。
function employeeSettingsToken(settingsKey: string): string | null {
  const token = getSessionToken()
  return getPageSessionScope() === 'employee' && !settingsKey.startsWith('platform-')
    && token && !isEmployeeSessionRetiring(token) ? token : null
}

export const viewSettingsApi: ViewSettingsApi = {
  async get(settingsKey) {
    const token = employeeSettingsToken(settingsKey)
    if (!token) return null
    try {
      const record = await callFunction<ViewSettingsOutput>('view-settings-get', {
        data: { settingsKey },
      }, { token, redirectOnAuthError: false })
      // 旧会话的迟到设置不能恢复到退出中或后来登录的身份。
      return employeeSettingsToken(settingsKey) === token ? record : null
    } catch {
      return null
    }
  },
  async save(input) {
    const token = employeeSettingsToken(input.settingsKey)
    if (!token) return
    try {
      await callFunction<ViewSettingsOutput>('view-settings-save', {
        data: {
          settingsKey: input.settingsKey,
          fieldJson: input.fieldJson ?? undefined,
          columnJson: input.columnJson ?? undefined,
        },
      }, { token, redirectOnAuthError: false })
    } catch {
      /* 静默失败，localStorage 兜底 */
    }
  },
}
